/**
 * Variable Type Registry Types
 *
 * This file defines the type system for template variables and automation output schemas.
 * It enables type-safe references to reusable complex types like lineItem, address, etc.
 *
 * Two enums are the core of this system:
 * - VariableTypeKey: All available complex type names (LINE_ITEM, ADDRESS, etc.)
 * - VariableFieldType: All field types including primitives and compound types
 */

/**
 * Enum of all available variable types - SINGLE SOURCE OF TRUTH
 * Use this enum everywhere instead of raw strings for type safety.
 *
 * To add a new type:
 * 1. Add it to this enum
 * 2. Create the definition in variable-type-definitions.ts
 * 3. Add it to VARIABLE_TYPE_REGISTRY
 */
export enum VariableTypeKey {
	// Common reusable types
	ADDRESS = 'address',
	CONTACT = 'contact',

	// Entity types
	LINE_ITEM = 'lineItem',
	ORDER = 'order',
	CLIENT = 'client',
	QUOTE = 'quote',
}

/**
 * All field types for variable properties.
 * Includes both primitive types and compound types (array/object).
 */
export enum VariableFieldType {
	// Primitive types
	STRING = 'string',
	NUMBER = 'number',
	BOOLEAN = 'boolean',
	DATE = 'date',
	CURRENCY = 'currency',
	EMAIL = 'email',
	PHONE = 'phone',

	// Compound types (can reference other types via arrayType/objectType)
	ARRAY = 'array',
	OBJECT = 'object',
}

/**
 * Definition for a single property within a complex type.
 *
 * For primitive types (STRING, NUMBER, etc.), just set the type.
 * For ARRAY types, also set arrayType to indicate what type the array contains.
 * For OBJECT types, also set objectType to indicate what type the object is.
 *
 * You can also use inline `properties` for one-off structures that don't warrant
 * a full type definition in the registry.
 */
export interface VariableTypeProperty {
	/** The field type (primitive or compound) */
	type: VariableFieldType;

	/** Human-readable label for display */
	label: string;

	/** Optional description explaining the field */
	description?: string;

	/**
	 * For ARRAY types: This array contains items of type X.
	 * Use VariableTypeKey enum to reference a type from the registry.
	 */
	arrayType?: VariableTypeKey;

	/**
	 * For OBJECT types: This object is of type Y.
	 * Use VariableTypeKey enum to reference a type from the registry.
	 */
	objectType?: VariableTypeKey;

	/**
	 * Inline schema for one-off structures.
	 * Use this instead of arrayType/objectType when the structure
	 * doesn't warrant a full type definition in the registry.
	 */
	properties?: Record<string, VariableTypeProperty>;
}

/**
 * A complete type definition that can be referenced by VariableTypeKey.
 */
export interface VariableTypeDefinition {
	/** The unique key for this type (matches VariableTypeKey enum) */
	typeKey: VariableTypeKey;

	/** Human-readable label for display */
	label: string;

	/** Optional description explaining the type */
	description?: string;

	/** The properties/fields that make up this type */
	properties: Record<string, VariableTypeProperty>;
}

/**
 * The complete registry of all variable types.
 * Keyed by VariableTypeKey enum for type safety.
 */
export type VariableTypeRegistry = Record<
	VariableTypeKey,
	VariableTypeDefinition
>;
